import React, { useState, useEffect } from 'react';
import { DynamicIcon } from '../icons/DynamicIcon';
import { calcularOrcamentoMultiplo, formatarMoeda } from '../../services/orcamentoService';
import toast from 'react-hot-toast';

const OrcamentoComparativo = ({ drinks, eventoData }) => {
  const [orcamento, setOrcamento] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mostrarDetalhes, setMostrarDetalhes] = useState(false);

  useEffect(() => {
    if (drinks && drinks.length > 0) {
      carregarOrcamento();
    }
  }, [drinks]);

  const carregarOrcamento = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Formatar drinks para o formato esperado pela função
      const drinksFormatados = drinks.map(d => ({
        id: d.id,
        quantidade: d.quantity
      }));
      
      const resultado = await calcularOrcamentoMultiplo(drinksFormatados, 'both');
      setOrcamento(resultado);
    } catch (err) {
      console.error('Erro ao calcular orçamento:', err);
      setError('Não foi possível calcular o orçamento');
      toast.error('Erro ao calcular orçamento');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center py-8">
          <DynamicIcon name="loader" size={24} className="animate-spin text-blue-600 mr-2" />
          <span className="text-gray-600">Calculando orçamento...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center text-red-600 mb-4">
          <DynamicIcon name="alertCircle" size={20} className="mr-2" />
          <span>{error}</span>
        </div>
        <button
          onClick={carregarOrcamento}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Tentar Novamente
        </button>
      </div>
    );
  }

  if (!orcamento) return null;

  const { standard, premium, diferenca, percentual } = orcamento;

  // Calcular custos adicionais
  const calcularCustoEquipe = () => {
    const duracao = calcularDuracaoHoras();
    const custoBartenders = eventoData.bartenders * 50 * duracao; // R$ 50/hora por bartender
    const custoAssistentes = eventoData.assistentes * 30 * duracao; // R$ 30/hora por assistente
    return custoBartenders + custoAssistentes;
  };

  const calcularCustoComplementos = () => {
    // Implementação simplificada
    return eventoData.numeroConvidados * 5; // R$ 5 por convidado para complementos
  };

  const calcularDuracaoHoras = () => {
    if (eventoData.horarioInicio && eventoData.horarioTermino) {
      const [horaInicio, minInicio] = eventoData.horarioInicio.split(':').map(Number);
      const [horaFim, minFim] = eventoData.horarioTermino.split(':').map(Number);
      
      let inicioMinutos = horaInicio * 60 + minInicio;
      let fimMinutos = horaFim * 60 + minFim;
      
      if (fimMinutos < inicioMinutos) {
        fimMinutos += 24 * 60;
      }
      
      return (fimMinutos - inicioMinutos) / 60;
    }
    return 4; // Duração padrão
  };

  // Calcular subtotais e totais
  const custoEquipe = calcularCustoEquipe();
  const custoComplementos = calcularCustoComplementos();
  
  const subtotalStandard = standard.custoTotal + custoEquipe + custoComplementos;
  const subtotalPremium = premium.custoTotal + custoEquipe + custoComplementos;
  
  const margemLucro = eventoData.margemLucro / 100;
  const totalStandard = subtotalStandard * (1 + margemLucro);
  const totalPremium = subtotalPremium * (1 + margemLucro);

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium mb-4 flex items-center">
        <DynamicIcon name="fileText" size={20} className="mr-2 text-blue-600" />
        Orçamento Comparativo
      </h3>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="col-span-3 md:col-span-1">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Detalhes do Evento</h4>
            <div className="space-y-1 text-sm">
              <p><span className="text-gray-600">Convidados:</span> {eventoData.numeroConvidados}</p>
              <p><span className="text-gray-600">Duração:</span> {calcularDuracaoHoras().toFixed(1)}h</p>
              <p><span className="text-gray-600">Bartenders:</span> {eventoData.bartenders}</p>
              <p><span className="text-gray-600">Assistentes:</span> {eventoData.assistentes}</p>
            </div>
          </div>
        </div>

        {/* Standard */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium text-green-800 flex items-center">
              <DynamicIcon name="check" size={16} className="mr-1 text-green-600" />
              Standard
            </h4>
            <span className="text-lg font-bold text-green-700">
              {formatarMoeda(totalStandard)}
            </span>
          </div>
          <div className="space-y-1 text-sm text-green-700">
            <p>Drinks: {formatarMoeda(standard.custoTotal)}</p>
            <p>Equipe: {formatarMoeda(custoEquipe)}</p>
            <p>Complementos: {formatarMoeda(custoComplementos)}</p>
            <p className="pt-1 border-t border-green-200 font-medium">
              Subtotal: {formatarMoeda(subtotalStandard)}
            </p>
            <p>
              Margem ({eventoData.margemLucro}%): {formatarMoeda(subtotalStandard * margemLucro)}
            </p>
          </div>
        </div>

        {/* Premium */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium text-amber-800 flex items-center">
              <DynamicIcon name="star" size={16} className="mr-1 text-amber-600" />
              Premium
            </h4>
            <span className="text-lg font-bold text-amber-700">
              {formatarMoeda(totalPremium)}
            </span>
          </div>
          <div className="space-y-1 text-sm text-amber-700">
            <p>Drinks: {formatarMoeda(premium.custoTotal)}</p>
            <p>Equipe: {formatarMoeda(custoEquipe)}</p>
            <p>Complementos: {formatarMoeda(custoComplementos)}</p>
            <p className="pt-1 border-t border-amber-200 font-medium">
              Subtotal: {formatarMoeda(subtotalPremium)}
            </p>
            <p>
              Margem ({eventoData.margemLucro}%): {formatarMoeda(subtotalPremium * margemLucro)}
            </p>
          </div>
        </div>
      </div>

      {/* Diferença */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between mb-1">
          <h4 className="font-medium text-blue-800">Diferença Total</h4>
          <span className="font-bold text-blue-700">
            {formatarMoeda(totalPremium - totalStandard)} ({percentual}%)
          </span>
        </div>
        <p className="text-sm text-blue-700">
          A opção Premium é {percentual}% mais cara que a Standard
        </p>
      </div>

      {/* Toggle Detalhes */}
      <button
        onClick={() => setMostrarDetalhes(!mostrarDetalhes)}
        className="flex items-center text-blue-600 hover:text-blue-800"
      >
        <DynamicIcon 
          name={mostrarDetalhes ? "chevronUp" : "chevronDown"} 
          size={16} 
          className="mr-1" 
        />
        {mostrarDetalhes ? "Ocultar detalhes dos drinks" : "Mostrar detalhes dos drinks"}
      </button>

      {/* Detalhes dos Drinks */}
      {mostrarDetalhes && (
        <div className="mt-4 border-t pt-4">
          <h4 className="font-medium mb-3">Detalhes dos Drinks</h4>
          
          <div className="space-y-4">
            {standard.drinks.map((drinkStandard, index) => {
              const drinkPremium = premium.drinks[index];
              
              return (
                <div key={index} className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 font-medium">
                    {drinkStandard.drink.nome}
                  </div>
                  
                  <div className="p-4">
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="bg-green-50 p-3 rounded-lg">
                        <div className="flex justify-between mb-1">
                          <span className="text-green-800 font-medium">Standard</span>
                          <span className="text-green-700 font-medium">
                            {formatarMoeda(drinkStandard.custoTotal)}
                          </span>
                        </div>
                      </div>
                      
                      <div className="bg-amber-50 p-3 rounded-lg">
                        <div className="flex justify-between mb-1">
                          <span className="text-amber-800 font-medium">Premium</span>
                          <span className="text-amber-700 font-medium">
                            {formatarMoeda(drinkPremium.custoTotal)}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Ingrediente
                            </th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Qtd.
                            </th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-green-600 uppercase tracking-wider">
                              Standard
                            </th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-amber-600 uppercase tracking-wider">
                              Premium
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {drinkStandard.detalhes.map((item, i) => {
                            const premiumItem = drinkPremium.detalhes[i];
                            
                            return (
                              <tr key={i}>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                                  {item.ingrediente}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-gray-500">
                                  {item.quantidade} {item.unidade}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-green-600">
                                  {formatarMoeda(item.custo)}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-amber-600">
                                  {formatarMoeda(premiumItem.custo)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrcamentoComparativo;